// src/auth/useAxios.ts
import axios, { AxiosInstance } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../KeycloakProvider";
import { getEnvironment, updateEnvironment } from "./environment";

/**
 * Creates a preconfigured axios wrapper that has authorization for
 * backend service (Keycloak JWT)
 */
export const useAxios = (): {
  axios: AxiosInstance | undefined;
} => {
  const { authenticated, initialized, token } = useAuth();
  const [jwt, setJwt] = useState<string>("");

  /**
   * Ensure we only set JWT when Keycloak is ready
   */
  useEffect(() => {
    if (!initialized) return;

    if (authenticated && token) {
      setJwt(token);
    } else {
      setJwt("");
    }
  }, [initialized, authenticated, token]);

  return useMemo(() => {
    let localInstance: AxiosInstance | undefined;

    // reuse cached instance if available
    if (getEnvironment().axios) {
      return { axios: getEnvironment().axios };
    }

    /**
     * Authenticated user → wait until JWT exists
     */
    if (initialized && authenticated && jwt) {
      localInstance = axios.create({
        baseURL: import.meta.env.VITE_BACKEND_URL,
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json;charset=UTF-8",
        },
      });
    } else if (initialized && !authenticated) {
      /**
       * Anonymous user
       */
      localInstance = axios.create({
        baseURL: import.meta.env.VITE_BACKEND_URL,
        headers: {
          Authorization: undefined,
          "Content-Type": "application/json;charset=UTF-8",
        },
      });
    }

    /**
     * Attach response interceptor once
     */
    localInstance?.interceptors.response.use((response) => {
      handleResponse(response.data);
      return response;
    });

    updateEnvironment({ axios: localInstance });

    return { axios: localInstance };
  }, [initialized, authenticated, jwt]);
};

/**
 * Helper to check if given value is an iso date string
 *
 * @param value any form of value to check
 * @returns true if value is iso date string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isIsoDateString = (value: any): boolean => {
  //regex to check for iso date YYYY-MM-DDThh:mm:ss.mmm+hh:mm from firebase
  const firebaseDateFormat = /\d{4}-\d{2}-\d{2}T[0-2]\d:[0-5]\d:[0-5]\dZ/;
  const isoDateFormat = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/;
  return (
    value &&
    typeof value === "string" &&
    (firebaseDateFormat.test(value) || isoDateFormat.test(value))
  );
};

/**
 * Helper to find all iso dates and maps in an object after receiving it from request
 * and parsing them
 *
 * @param body request body to check for dates and parse them
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleResponse = (body: any): void => {
  if (body === null || body === undefined || typeof body !== "object")
    return body;
  for (const key of Object.keys(body)) {
    const value = body[key];
    if (isIsoDateString(value)) body[key] = new Date(value);
    else if (typeof value === "object") handleResponse(value);
  }
  // Detects the room model
  if ("formerNamesMap" in body) {
    body["formerNamesMap"] = new Map<string, string>(
      Object.entries(body["formerNamesMap"] as Record<string, string>),
    );
  }
  // Detects the CheckboxMatrixItem model
  if ("monteurNamesMap" in body) {
    body["monteurNamesMap"] = new Map<string, string>(
      Object.entries(body["monteurNamesMap"] as Record<string, string>),
    );
  }
  // Detects the project model
  if ("checklistIdMap" in body) {
    body["checklistIdMap"] = new Map<string, number>(
      Object.entries(body["checklistIdMap"] as Record<string, number>),
    );
  }
  // Detects the protocol model
  if ("checkCandidateMap" in body) {
    body["checkCandidateMap"] = new Map<string, string>(
      Object.entries(body["checkCandidateMap"] as Record<string, string>),
    );
  }
  // Detects the conditionsMap model
  if ("conditionsMap" in body) {
    body["conditionsMap"] = new Map<string, string>(
      Object.entries(body["conditionsMap"] as Record<string, string>),
    );
  }
};
